import React, { useState, useRef, useEffect } from 'react';
import { 
  Languages,
  Upload,
  Download,
  Copy,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Globe,
  Eye,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Bookmark,
  Star,
  Share,
  AlertCircle,
  Info,
  Target,
  TrendingUp,
  BarChart3,
  PieChart,
  Brain,
  Zap,
  Shield,
  Lock,
  Key,
  Users,
  Building,
  Calendar,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Archive,
  Send,
  MessageSquare,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  TrendingDown,
  Settings,
  Cog,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Receipt,
  CreditCard,
  Calculator,
  Users2,
  Mic,
  Play,
  Pause,
  Square
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';

const MultilingualTranslation = () => {
  const { success, error: showError, info } = useToast();
  const [activeTab, setActiveTab] = useState('translate');
  const [isTranslating, setIsTranslating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguage, setTargetLanguage] = useState('es');
  const [translationMode, setTranslationMode] = useState('document');
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Backend integration states
  const [stats, setStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [isTestMode, setIsTestMode] = useState(process.env.NODE_ENV === 'development');
  
  const fileInputRef = useRef(null);

  // Data fetching functions
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await AIService.getTranslationStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching translation stats:', error);
      // Show empty data on error
      setStats({
        totalTranslations: 0,
        completedTranslations: 0,
        processingTranslations: 0,
        errorTranslations: 0,
        averageAccuracy: 0,
        totalWords: 0,
        languagesSupported: 15
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAnalytics = async (timeframe = selectedTimeframe) => {
    try {
      setAnalyticsLoading(true);
      const response = await AIService.getTranslationAnalytics(timeframe);
      if (response.success) {
        setAnalyticsData(response.data);
      }
    } catch (error) {
      console.error('Error fetching translation analytics:', error);
      // Show empty data on error
      setAnalyticsData({
        totalTranslations: 0,
        completedTranslations: 0,
        processingTranslations: 0,
        errorTranslations: 0,
        avgAccuracy: 0,
        totalWords: 0,
        languageDistribution: {},
        documentTypeDistribution: {},
        qualityDistribution: { high: 0, medium: 0, low: 0 },
        avgProcessingTime: 0,
        dailyTrend: []
      });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleTestFeature = () => {
    if (activeTab === 'translate') {
      setSourceText('This is a sample contract for testing translation functionality. It contains legal terms and conditions that need to be translated accurately.');
      setSourceLanguage('en');
      setTargetLanguage('es');
      setTranslationMode('contract');
    } else if (activeTab === 'analytics') {
      fetchAnalytics(selectedTimeframe);
    }
  };

  // useEffect for initial data loading
  useEffect(() => {
    fetchStats();
    fetchTranslationHistory();
    fetchAnalytics(selectedTimeframe);
  }, []);

  // Translation history state
  const [translationHistory, setTranslationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Mock data for initial display (will be replaced by backend data)
  const mockHistory = [
    {
      id: 'TRANS-001',
      documentName: 'Service Level Agreement - English',
      sourceLanguage: 'English',
      targetLanguage: 'Spanish',
      documentType: 'Contract',
      status: 'Completed',
      accuracy: 96,
      wordCount: 1250,
      translatedAt: '2024-02-16T10:30:00Z',
      translatedBy: 'AI Translation Engine',
      processingTime: '2.3s',
      confidence: 94,
      isActive: true,
      tags: ['contract', 'sla', 'translated'],
      createdBy: 'AI Translation',
      department: 'Legal',
      isBookmarked: true,
      isStarred: false,
      viewCount: 45,
      downloadCount: 8,
      fileSize: '45.2 KB',
      quality: 'High',
      reviewStatus: 'Approved'
    },
    {
      id: 'TRANS-002',
      documentName: 'Asset Purchase Agreement - French',
      sourceLanguage: 'French',
      targetLanguage: 'English',
      documentType: 'Contract',
      status: 'Processing',
      accuracy: 0,
      wordCount: 2100,
      translatedAt: '2024-02-16T11:15:00Z',
      translatedBy: 'AI Translation Engine',
      processingTime: null,
      confidence: 0,
      isActive: true,
      tags: ['contract', 'purchase', 'translated'],
      createdBy: 'AI Translation',
      department: 'Legal',
      isBookmarked: false,
      isStarred: true,
      viewCount: 12,
      downloadCount: 0,
      fileSize: '78.5 KB',
      quality: 'Medium',
      reviewStatus: 'Pending'
    },
    {
      id: 'TRANS-003',
      documentName: 'Employment Contract - German',
      sourceLanguage: 'German',
      targetLanguage: 'English',
      documentType: 'Contract',
      status: 'Error',
      accuracy: 0,
      wordCount: 1800,
      translatedAt: '2024-02-16T09:45:00Z',
      translatedBy: 'AI Translation Engine',
      processingTime: '1.8s',
      confidence: 0,
      isActive: true,
      tags: ['contract', 'employment', 'translated'],
      createdBy: 'AI Translation',
      department: 'HR',
      isBookmarked: false,
      isStarred: false,
      viewCount: 8,
      downloadCount: 0,
      fileSize: '62.1 KB',
      quality: 'Low',
      reviewStatus: 'Failed'
    }
  ];

  // Use real backend data, fallback to empty data if not available
  const displayStats = stats || {
    totalTranslations: 0,
    completedTranslations: 0,
    processingTranslations: 0,
    errorTranslations: 0,
    averageAccuracy: 0,
    totalWords: 0,
    languagesSupported: 15
  };

  const tabs = [
    { id: 'translate', label: 'Translation' },
    { id: 'batch', label: 'Batch Translation' },
    { id: 'history', label: 'Translation History' },
    { id: 'quality', label: 'Quality Check' },
    { id: 'analytics', label: 'Analytics' }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
    { code: 'no', name: 'Norwegian', flag: '🇳🇴' }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'documentName',
      label: 'Document',
      width: '300px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.sourceLanguage} → {row.targetLanguage} • {row.wordCount} words • {row.fileSize}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status, row) => {
        const statusConfig = {
          'Completed': { color: '#10b981', bg: '#d1fae5', icon: <CheckCircle size={14} /> },
          'Processing': { color: '#f59e0b', bg: '#fef3c7', icon: <RefreshCw size={14} /> },
          'Error': { color: '#ef4444', bg: '#fee2e2', icon: <XCircle size={14} /> }
        };
        const config = statusConfig[status] || statusConfig['Processing'];
        
        return (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '0',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color,
            width: 'fit-content'
          }}>
            {config.icon}
            {status}
          </span>
        );
      }
    },
    {
      key: 'accuracy',
      label: 'Accuracy',
      width: '120px',
      render: (accuracy) => {
        if (accuracy === 0) return <span style={{ color: '#6b7280' }}>-</span>;
        
        const getAccuracyColor = (accuracy) => {
          if (accuracy >= 90) return '#10b981';
          if (accuracy >= 80) return '#f59e0b';
          return '#ef4444';
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${accuracy}%`,
                height: '100%',
                backgroundColor: getAccuracyColor(accuracy),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: getAccuracyColor(accuracy) }}>
              {accuracy}%
            </span>
          </div>
        );
      }
    },
    {
      key: 'quality',
      label: 'Quality',
      width: '100px',
      render: (quality) => {
        const qualityConfig = {
          'High': { color: '#10b981', bg: '#d1fae5' },
          'Medium': { color: '#f59e0b', bg: '#fef3c7' },
          'Low': { color: '#ef4444', bg: '#fee2e2' }
        };
        const config = qualityConfig[quality] || qualityConfig['Medium'];
        
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color
          }}>
            {quality}
          </span>
        );
      }
    },
    {
      key: 'translatedAt',
      label: 'Translated',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  // Fetch translation history from backend
  const fetchTranslationHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await AIService.getTranslationHistory({ limit: 100 });
      if (response.success && response.data) {
        // Format backend data to match frontend structure
        const formattedHistory = response.data.map(trans => ({
          id: trans.translationId || trans._id,
          _id: trans._id,
          documentName: trans.title || trans.sourceText?.substring(0, 50) + '...',
          sourceLanguage: languages.find(l => l.code === trans.sourceLanguage)?.name || trans.sourceLanguage,
          targetLanguage: languages.find(l => l.code === trans.targetLanguage)?.name || trans.targetLanguage,
          documentType: trans.documentType || 'Document',
          status: trans.status || 'Completed',
          accuracy: trans.accuracy || trans.confidence || 0,
          wordCount: trans.sourceWordCount || 0,
          translatedAt: trans.translatedAt || trans.createdAt,
          translatedBy: trans.createdBy?.name || 'AI Translation Engine',
          processingTime: trans.processingTime ? `${trans.processingTime}s` : null,
          confidence: trans.confidence || 0,
          isActive: true,
          tags: trans.tags || [],
          createdBy: trans.createdBy?.name || 'AI Translation',
          department: 'Legal',
          isBookmarked: trans.isBookmarked || false,
          isStarred: trans.isStarred || false,
          viewCount: 0,
          downloadCount: 0,
          fileSize: trans.documentFile?.size ? `${(trans.documentFile.size / 1024).toFixed(1)} KB` : `${(trans.sourceText?.length / 1024 || 0).toFixed(1)} KB`,
          quality: trans.qualityScore >= 90 ? 'High' : trans.qualityScore >= 70 ? 'Medium' : 'Low',
          reviewStatus: 'Pending'
        }));
        setTranslationHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error fetching translation history:', error);
      showError('Failed to load translation history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handler functions
  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      showError('Please enter text to translate');
      return;
    }
    
    setIsTranslating(true);
    try {
      const response = await AIService.translateText(sourceText, sourceLanguage, targetLanguage, translationMode === 'document' ? 'Document' : 'General');
      
      if (response.success) {
        setTranslatedText(response.data.translatedText);
        success('Translation completed successfully!');
        
        // Refresh history and stats after successful translation
        await fetchTranslationHistory();
        await fetchStats();
      } else {
        throw new Error('Translation failed');
      }
    } catch (error) {
      console.error('Error translating text:', error);
      showError(error.message || 'Failed to translate text. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      // Read file content
      const reader = new FileReader();
      reader.onload = (e) => {
        setSourceText(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleView = (translation) => {
    //console.log('Viewing translation:', translation.documentName);
  };

  const handleDownload = (translation) => {
    //console.log('Downloading translation:', translation.documentName);
  };

  const handleDelete = (translation) => {
    setSelectedItem(translation);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    
    setIsDeleting(true);
    try {
      const translationId = selectedItem.id || selectedItem.translationId || selectedItem._id;
      await AIService.deleteTranslation(translationId);
      
      setTranslationHistory(prev => prev.filter(translation => 
        (translation.id !== translationId) && 
        (translation.translationId !== translationId) &&
        (translation._id !== translationId)
      ));
      setShowDeleteModal(false);
      setSelectedItem(null);
      success('Translation deleted successfully');
      
      // Refresh stats
      await fetchStats();
    } catch (error) {
      console.error('Error deleting translation:', error);
      showError('Failed to delete translation. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyTranslation = () => {
    navigator.clipboard.writeText(translatedText);
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Multilingual Translation"
        description="AI-powered translation for contracts, documents, and legal content across multiple languages"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
      {/* Tabbed Navigation */}
      <div className="project-tabs">
        <div className="project-tabs-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <div className="project-tab-icon">
                {tab.id === 'translate' && <Languages size={18} />}
                {tab.id === 'batch' && <Upload size={18} />}
                {tab.id === 'history' && <Clock size={18} />}
                {tab.id === 'quality' && <Target size={18} />}
                {tab.id === 'analytics' && <BarChart3 size={18} />}
              </div>
              <div className="project-tab-content">
                <div className="project-tab-label">{tab.label}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="page-stats-grid">
        <div className="page-kpi-card color-1">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">
                  {statsLoading ? '...' : displayStats.totalTranslations}
                </div>
                <div className="page-kpi-label">Total Translations</div>
              </div>
              <div className="page-kpi-icon-container">
                <Languages size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-2">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">
                  {statsLoading ? '...' : displayStats.languagesSupported}
                </div>
                <div className="page-kpi-label">Languages Supported</div>
              </div>
              <div className="page-kpi-icon-container">
                <Globe size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-3">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">
                  {statsLoading ? '...' : `${Math.round(displayStats.averageAccuracy)}%`}
                </div>
                <div className="page-kpi-label">Avg. Accuracy</div>
              </div>
              <div className="page-kpi-icon-container">
                <Target size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-4">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">
                  {statsLoading ? '...' : displayStats.totalWords.toLocaleString()}
                </div>
                <div className="page-kpi-label">Words Translated</div>
              </div>
              <div className="page-kpi-icon-container">
                <FileText size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Translation Tab */}
      {activeTab === 'translate' && (
        <div>
          {/* Translation Interface */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Languages size={20} style={{ color: 'var(--primary-color)' }} />
              Text Translation
            </h3>
            
            {/* Language Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Source Language
                </label>
                <select
                  value={sourceLanguage}
                  onChange={(e) => setSourceLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Target Language
                </label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Document Type
                </label>
                <select
                  value={translationMode}
                  onChange={(e) => setTranslationMode(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="document">Document</option>
                  <option value="contract">Contract</option>
                  <option value="legal">Legal Text</option>
                  <option value="general">General Text</option>
                </select>
              </div>
            </div>

            {/* File Upload */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Upload Document (Optional)
              </label>
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: '0',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color 0.2s ease'
              }}
              onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={24} style={{ color: '#6b7280', marginBottom: '8px' }} />
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>
                  Click to upload or drag and drop
                </p>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                  Supports .txt, .doc, .docx, .pdf files
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.doc,.docx,.pdf"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
              {uploadedFile && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '0',
                  border: '1px solid var(--primary-color)20'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileText size={16} style={{ color: 'var(--primary-color)' }} />
                    <span style={{ fontSize: '14px', color: 'var(--primary-color)', fontWeight: '500' }}>
                      {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '24px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Source Text
                </label>
                <textarea
                  value={sourceText}
                  onChange={(e) => setSourceText(e.target.value)}
                  placeholder="Enter text to translate..."
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    resize: 'vertical',
                    lineHeight: '1.5'
                  }}
                />
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px'
                }}>
                  {sourceText.length} characters, {sourceText.split(' ').filter(w => w.length > 0).length} words
                </div>
              </div>

              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Translated Text
                  </label>
                  {translatedText && (
                    <button
                      onClick={handleCopyTranslation}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        color: 'var(--primary-color)',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--primary-color)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Copy size={12} />
                      Copy
                    </button>
                  )}
                </div>
                <textarea
                  value={translatedText}
                  readOnly
                  placeholder="Translated text will appear here..."
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    resize: 'vertical',
                    lineHeight: '1.5',
                    backgroundColor: '#f9fafb'
                  }}
                />
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px'
                }}>
                  {translatedText.length} characters, {translatedText.split(' ').filter(w => w.length > 0).length} words
                </div>
              </div>
            </div>

            {/* Translate Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '24px'
            }}>
              <button
                onClick={handleTranslate}
                disabled={!sourceText.trim() || isTranslating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 32px',
                  backgroundColor: !sourceText.trim() || isTranslating ? '#9ca3af' : 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: !sourceText.trim() || isTranslating ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  transition: 'all 0.2s ease'
                }}
              >
                {isTranslating ? (
                  <>
                    <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    Translating...
                  </>
                ) : (
                  <>
                    <Languages size={20} />
                    Translate Text
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translation History Tab */}
      {activeTab === 'history' && (
        <div>
          {historyLoading ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid var(--primary-color)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#6b7280',
                margin: 0
              }}>
                Loading translation history...
              </h4>
            </div>
          ) : (
            <div>
              {/* History DataTable */}
              <div className="page-table-container">
                <DataTable
                  data={translationHistory}
                  columns={columns}
                  title="Translation History"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={true}
                  showCheckboxes={false}
                  onView={handleView}
                  onEdit={handleDownload}
                  onDelete={handleDelete}
                  searchPlaceholder="Search translation history..."
                  emptyMessage="No translation history found. Start translating documents to see history."
                  initialHiddenColumns={['id']}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Other Tabs */}
      {activeTab === 'batch' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Batch Translation Interface Coming Soon!
        </div>
      )}

      {activeTab === 'quality' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Quality Check Interface Coming Soon!
        </div>
      )}

      {activeTab === 'analytics' && (
        <div>
          {/* Analytics Header */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <BarChart3 size={24} style={{ color: 'var(--primary-color)' }} />
                Translation Analytics
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Comprehensive insights into translation performance and usage patterns
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <select
                value={selectedTimeframe}
                onChange={(e) => {
                  setSelectedTimeframe(e.target.value);
                  fetchAnalytics(e.target.value);
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button
                onClick={() => fetchAnalytics(selectedTimeframe)}
                disabled={analyticsLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: analyticsLoading ? '#9ca3af' : 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: analyticsLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <RefreshCw size={16} style={{ animation: analyticsLoading ? 'spin 1s linear infinite' : 'none' }} />
                Refresh
              </button>
            </div>
          </div>

          {/* Analytics Content */}
          {analyticsLoading ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '40px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--primary-color)' }} />
              <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>Loading analytics data...</p>
            </div>
          ) : analyticsData ? (
            <div style={{ display: 'grid', gap: '24px' }}>
              {/* Key Performance Metrics */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 20px 0'
                }}>
                  Key Performance Metrics
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '20px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)', marginBottom: '4px' }}>
                      {analyticsData.totalTranslations}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Translations</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', marginBottom: '4px' }}>
                      {analyticsData.completedTranslations}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Completed</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b', marginBottom: '4px' }}>
                      {Math.round(analyticsData.avgAccuracy)}%
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Avg. Accuracy</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6', marginBottom: '4px' }}>
                      {analyticsData.totalWords.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Words Translated</div>
                  </div>
                </div>
              </div>

              {/* Language Distribution */}
              {analyticsData.languageDistribution && Object.keys(analyticsData.languageDistribution).length > 0 && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '24px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 20px 0'
                  }}>
                    Language Distribution
                  </h4>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {Object.entries(analyticsData.languageDistribution).map(([language, count]) => (
                      <div key={language} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px'
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{language}</span>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>{count} translations</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quality Distribution */}
              {analyticsData.qualityDistribution && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '24px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 20px 0'
                  }}>
                    Quality Distribution
                  </h4>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#d1fae5',
                      borderRadius: '8px'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#065f46' }}>High Quality</span>
                      <span style={{ fontSize: '14px', color: '#065f46' }}>{analyticsData.qualityDistribution.high} translations</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '8px'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#92400e' }}>Medium Quality</span>
                      <span style={{ fontSize: '14px', color: '#92400e' }}>{analyticsData.qualityDistribution.medium} translations</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#fee2e2',
                      borderRadius: '8px'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#991b1b' }}>Low Quality</span>
                      <span style={{ fontSize: '14px', color: '#991b1b' }}>{analyticsData.qualityDistribution.low} translations</span>
                    </div>
                  </div>
                </div>
              )}

              {/* System Performance */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 20px 0'
                }}>
                  System Performance
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '20px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)', marginBottom: '4px' }}>
                      {analyticsData.avgProcessingTime?.toFixed(1)}s
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Avg. Processing Time</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', marginBottom: '4px' }}>
                      99.9%
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>System Uptime</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444', marginBottom: '4px' }}>
                      {analyticsData.errorTranslations}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Error Rate</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '40px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <BarChart3 size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
                No Analytics Data Available
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px 0' }}>
                Start translating documents to see analytics and insights.
              </p>
              <button
                onClick={() => fetchAnalytics(selectedTimeframe)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  margin: '0 auto'
                }}
              >
                <RefreshCw size={16} />
                Load Analytics
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Translation"
        message={`Are you sure you want to delete "${selectedItem?.documentName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      <style jsx="true">{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      </div>
      <ToastContainer />
    </>
  );
};

export default MultilingualTranslation;
